import { observer } from 'mobx-react-lite'
import React, { useReducer, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { RADOM_CUSTOMER_CREATION } from '../util/BoilerPlate'
import { PrimaryButton, SecondaryButton } from './Button'
import { Chevron } from '../icons/Chevron'
import { InputLabel, OptionalLabel, TextInputWithLabel } from './Input'
import Radom, { Customer } from '../state/Radom'
import { SwitchWithLabel } from './Switch'
import { CustomerType, oldestPermittedDateOfBirth, youngestPermittedDateOfBirth } from './TravelRuleFieldsWidget'
import { errorToast, isNotEmpty, simpleEmailValidation, splitDateTime, successToast } from '../util/Util'
import { RADOM_COLORS } from '../util/Constants'
import DateSelector2 from './DateSelector2'
import CountrySelector from './CountrySelector'

const Form = styled.form`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  font-size: 14px;
`
const CustomerFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: auto;
  max-width: 520px;
  font-size: 14px;
`

const errorToastStyle = {
  backgroundColor: RADOM_COLORS.ERROR,
  padding: 15,
  marginBottom: 10,
  borderRadius: 5,
  fontSize: 14,
  color: 'white'
}

const additionalFieldsStyle = {
  display: 'flex',
  border: 0,
  alignItems: 'center',
  gap: 5,
  padding: 0,
  marginTop: 15,
  color: RADOM_COLORS.BLACK,
  opacity: 0.5
}

// ---------------------------------------------------------------------------------------------------------------------
// Customer information collected either at creation time or when that customer performs a checkout of some type
export interface CustomerFields {
  name: string
  email: string
  residenceCountry: string
  companyName: string
  dob: string | undefined
  billingAddress: string
  phone: string
  telegram: string
  discord: string
  accountType: string
}

const emptyCustomer = (): CustomerFields => {
  return {
    name: '',
    email: '',
    residenceCountry: '',
    companyName: '',
    dob: undefined,
    billingAddress: '',
    phone: '',
    telegram: '',
    discord: '',
    accountType: ''
  }
}

// ---------------------------------------------------------------------------------------------------------------------
enum FormFieldTypes {
  TEXT_INPUT_WITH_LABEL,
  DATE_SELECTOR2,
  COUNTRY_SELECTOR,
}

interface FormFieldMetadata {
  // Is field mandatory
  isRequired: boolean

  // Is field displayed only when "Show more fields" is selected
  isAdditional: boolean

  // Is field applicable only to a business customer
  isBusiness: boolean

  // The type of UI element used to render this property
  elementType: FormFieldTypes

  // propertyName value must correspond to a field in teh interface CustomerFields
  propertyName: string

  // An array of two elements.
  // labelText[0] = The label text for an individual customer
  // labelText[1] = The label text for a business customer
  labelText: string[]

  // Any function capable of validating the field's content
  validatorFn: (arg: any) => boolean
}

// ---------------------------------------------------------------------------------------------------------------------
// Define all input fields for creating a customer
const inputFields: FormFieldMetadata[] = [
  {
    isRequired: true,
    isAdditional: false,
    isBusiness: false,
    elementType: FormFieldTypes.TEXT_INPUT_WITH_LABEL,
    propertyName: 'email',
    labelText: ['Email Address', 'Company Email Address'],
    validatorFn: simpleEmailValidation
  },
  {
    isRequired: true,
    isAdditional: false,
    isBusiness: true,
    elementType: FormFieldTypes.TEXT_INPUT_WITH_LABEL,
    propertyName: 'companyName',
    labelText: ['', 'Company Name'],
    validatorFn: isNotEmpty
  },
  {
    isRequired: false,
    isAdditional: false,
    isBusiness: false,
    elementType: FormFieldTypes.TEXT_INPUT_WITH_LABEL,
    propertyName: 'name',
    labelText: ['Name', 'Contact Name'],
    validatorFn: isNotEmpty
  },
  {
    isRequired: false,
    isAdditional: false,
    isBusiness: false,
    elementType: FormFieldTypes.DATE_SELECTOR2,
    propertyName: 'dob',
    labelText: ['Date of Birth', 'Contact Date of Birth'],
    validatorFn: isNotEmpty
  },
  {
    isRequired: false,
    isAdditional: false,
    isBusiness: false,
    elementType: FormFieldTypes.COUNTRY_SELECTOR,
    propertyName: 'residenceCountry',
    labelText: ['Country', 'Company Registration Country'],
    validatorFn: isNotEmpty
  },
  {
    isRequired: false,
    isAdditional: true,
    isBusiness: false,
    elementType: FormFieldTypes.TEXT_INPUT_WITH_LABEL,
    propertyName: 'billingAddress',
    labelText: ['Billing Address', 'Company Billing Address'],
    validatorFn: isNotEmpty
  },
  {
    isRequired: false,
    isAdditional: true,
    isBusiness: false,
    elementType: FormFieldTypes.TEXT_INPUT_WITH_LABEL,
    propertyName: 'phone',
    labelText: ['Phone No', 'Company Phone No'],
    validatorFn: isNotEmpty
  },
  {
    isRequired: false,
    isAdditional: true,
    isBusiness: false,
    elementType: FormFieldTypes.TEXT_INPUT_WITH_LABEL,
    propertyName: 'telegram',
    labelText: ['Telegram', 'Company Telegram'],
    validatorFn: isNotEmpty
  },
  {
    isRequired: false,
    isAdditional: true,
    isBusiness: false,
    elementType: FormFieldTypes.TEXT_INPUT_WITH_LABEL,
    propertyName: 'discord',
    labelText: ['Discord', 'Company Discord'],
    validatorFn: isNotEmpty
  }
]

const genInputLabel = (inFld: FormFieldMetadata, isBusiness): JSX.Element => {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '15px' }}>
    <InputLabel>{inFld.labelText[+isBusiness]}</InputLabel>
    {!inFld.isRequired && <OptionalLabel>Optional</OptionalLabel>}
  </div>
}

// ---------------------------------------------------------------------------------------------------------------------
const CustomerForm = observer((props: { onCreateCustomer?: (customer: Customer) => void }) => {
  const navigate = useNavigate()
  const [showMore, setShowMore] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [customerForm, updateCustomerForm] = useReducer((prev, next) => { return { ...prev, ...next } }, emptyCustomer())
  const [errorMessage, setErrorMessage] = useState<string>()

  // Start by assuming that the customer is an individual
  const [isBusiness, setIsBusiness] = useState(false)
  const toggleIsBusiness = (): void => {
    const newState = isBusiness

    // If isBusiness is switched off, explicitly drop any value that might exist in customerForm.companyName
    if (newState === false) {
      updateCustomerForm({ companyName: '' })
    }
    setIsBusiness(!newState)
  }

  // Render input fields
  const generateForm = (additionalFields: boolean): JSX.Element[] => {
    return inputFields
      .filter(inFld => inFld.isAdditional === additionalFields)
      .map(inFld => {
        const txt = inFld.labelText[+isBusiness]
        let el

        switch (inFld.elementType) {
          case FormFieldTypes.DATE_SELECTOR2:
            el = !inFld.isBusiness || isBusiness
              ? el = <>
                {genInputLabel(inFld, isBusiness)}
                <DateSelector2
                  value={customerForm[inFld.propertyName]}
                  placeholder={`Enter ${txt.toLowerCase()}`}
                  startDate={oldestPermittedDateOfBirth}
                  endDate={youngestPermittedDateOfBirth}
                  onChange={date => {
                    if (date) {
                      const newCustomerForm = { ...customerForm }
                      newCustomerForm[inFld.propertyName] = splitDateTime(date).datePart
                      updateCustomerForm(newCustomerForm)
                    }
                  }}
                />
              </>
              : <></>
            break

          // -----------------------------------------------------------------------------------------------------------
          case FormFieldTypes.COUNTRY_SELECTOR:
            el = !inFld.isBusiness || isBusiness
              ? <>
                {genInputLabel(inFld, isBusiness)}
                <CountrySelector
                  selectedCountry={customerForm[inFld.propertyName]}
                  onCountrySelected={country => {
                    if (country) {
                      const newCustomerForm = { ...customerForm }
                      newCustomerForm[inFld.propertyName] = country.name
                      updateCustomerForm(newCustomerForm)
                    }
                  }}
                />
              </>
              : <></>
            break

          // -----------------------------------------------------------------------------------------------------------
          // Default to a TextInputWithLabel
          default:
            el = !inFld.isBusiness || isBusiness
              ? <TextInputWithLabel
                value={customerForm[inFld.propertyName]}
                label={txt}
                placeholder={`Enter ${txt.toLowerCase()}`}
                required={inFld.isRequired}
                style={{ marginTop: 15 }}
                onInput={el => {
                  const newCustomerForm = { ...customerForm }
                  newCustomerForm[inFld.propertyName] = (el.target as HTMLInputElement).value
                  updateCustomerForm(newCustomerForm)
                }}
              />
              : <></>
        }

        return el
      })
  }

  // -------------------------------------------------------------------------------------------------------------------
  const onCreateCustomer = async (): Promise<void> => {
    setIsSubmitting(true)
    try {
      const customer = await Radom.createCustomer(customerForm)
      if (props.onCreateCustomer) {
        props.onCreateCustomer(customer)
      } else {
        navigate('/customers')
      }
      successToast('Successfully created user')
    } catch (err) {
      const msg = err.response.data.error ?? err.code
      errorToast(`Error: ${msg}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Create customer widget
  return <CustomerFieldsContainer>
    {
      // Error toast
      errorMessage && errorMessage.length > 0 &&
      <div style={errorToastStyle}>{errorMessage}</div>
    }

    {
      // Boilerplate text for collection of personal information
      <div style={{ display: 'flex', alignItems: 'center' }}>{RADOM_CUSTOMER_CREATION}</div>
    }

    {<SwitchWithLabel state={isBusiness} label='Customer is a business?' onClickAction={toggleIsBusiness} />}
    {generateForm(false)}
    {showMore && generateForm(showMore)}

    {
      // Add/Hide more fields
      <SecondaryButton
        onClick={() => setShowMore(!showMore)}
        type='button'
        style={additionalFieldsStyle}>
        <span>{showMore ? 'Hide' : 'Add'} more fields</span>
        <Chevron style={{ transform: `rotate(${showMore ? 180 : 0}deg)` }} />
      </SecondaryButton>
    }

    {
      // Create Customer button
      <PrimaryButton
        isLoading={isSubmitting}
        disabled={false}
        style={{ background: RADOM_COLORS.BLUE4, width: '100%', marginTop: '25px' }}
        onClick={() => {
          setErrorMessage('')
          customerForm.accountType = isBusiness ? CustomerType.Business : CustomerType.Individual

          const errorMsgs: string[] = inputFields
            .reduce((acc, inFld) => {
              // Is this field required but invalid?
              if (inFld.isRequired && !inFld.validatorFn(customerForm[inFld.propertyName])) {
                // Is the field required for a business customer?
                if (inFld.isBusiness && isBusiness) {
                  acc.push(inFld.labelText[+inFld.isBusiness])
                } else
                  // Is the field required for an individual customer?
                  if (!inFld.isBusiness) {
                    acc.push(inFld.labelText[+inFld.isBusiness])
                  }
              }

              return acc
            }, new Array())

          // Show any error messages?
          if (errorMsgs.length > 0) {
            setErrorMessage(
              `Please enter ${errorMsgs.length === 1 ? 'a valid value' : 'valid values'} for ${errorMsgs.join(', ')}`
            )
          } else {
            setErrorMessage('')
            onCreateCustomer()
          }
        }}
      >
        Create Customer
      </PrimaryButton>
    }
  </CustomerFieldsContainer >
})

export default CustomerForm
